import { useId, useState } from 'react';
import { endpoints } from '../services/api';
import useRetornos from './resultadosBancarios/useRetornos';
import {
    euros, fechaBancaria, fechaRecepcion, levantadoEfectivo, textoAviso,
    resultadoLevantamiento, textoEstado, errorRetorno,
} from './resultadosBancarios/retornosModel';
import './resultadosBancarios/resultadosBancarios.css';

function ErrorMessage({ error, children }) {
    if (!error) return null;
    return <div className="rb-message rb-error" role="alert">
        <strong>{error.status ? `Error ${error.status}` : 'Operación bloqueada'}{error.code ? ` · ${error.code}` : ''}</strong>
        <div>{error.message}</div>
        {children}
    </div>;
}

function Hash({ label, value }) {
    return <div className="rb-hash"><span>{label}</span><code>{value || 'No informado'}</code></div>;
}

export function ImportesRetorno({ data = {}, fase }) {
    const final = Number(fase) === 6;
    const metrics = [
        ['Ordenado', euros(data.ordenado_centimos)],
        ['Retenido comunicado', euros(data.retenido_centimos)],
        ['Levantamiento solicitado', final ? euros(data.levantamiento_centimos) : 'No aplica (F4)'],
        ['Levantado efectivo', final ? euros(levantadoEfectivo(data, fase)) : 'No aplica (F4)'],
        ['Neto final comunicado', final ? euros(data.neto_centimos) : 'Pendiente de F6'],
    ];
    return <dl className="rb-metrics">
        {metrics.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
    </dl>;
}

export function DetallesRetorno({ detalles = [], fase }) {
    if (!Array.isArray(detalles) || !detalles.length) return <p>No hay detalle comunicado.</p>;
    const final = Number(fase) === 6;
    return <div className="rb-diligencias">
        {detalles.map((d, index) => <details className="rb-diligencia" key={`${d.nif}-${d.identificador_deuda}-${index}`}>
            <summary>
                <span><strong>Diligencia {d.identificador_deuda || 'No informada'}</strong><span className="rb-subline">{d.nif} · {d.nombre}</span></span>
                <span className="rb-diligencia-amount">{final ? 'Neto' : 'Retenido'}: {euros(final ? d.neto_centimos : d.retenido_centimos)}</span>
            </summary>
            <div className="rb-diligencia-body">
                <p>Fecha de retención: <strong>{fechaBancaria(d.fecha_retencion)}</strong>{d.codigo_deuda && <> · Código de deuda: {d.codigo_deuda}</>}</p>
                <ImportesRetorno data={d} fase={fase} />
                <p><strong>Resultado de diligencia:</strong> {final ? resultadoLevantamiento(d.resultado_levantamiento) : 'Retención comunicada; resultado final pendiente de F6'}</p>
                <div className="rb-table-wrap" role="region" aria-label={`Cuentas de diligencia ${d.identificador_deuda}`} tabIndex={0}>
                    <table className="data-table rb-table rb-account-table">
                        <caption>Resultados por cuenta ({d.cuentas?.length || 0})</caption>
                        <thead><tr><th>Cuenta / IBAN</th><th>Resultado retención</th><th>Retenido</th><th>Levantado efectivo</th><th>Neto final</th><th>Resultado levantamiento</th></tr></thead>
                        <tbody>{(d.cuentas || []).map((c, i) => <tr key={`${c.iban}-${i}`}>
                            <td data-label="Cuenta / IBAN"><code>{c.iban}</code></td>
                            <td data-label="Resultado retención">Código {c.resultado ?? 'no comunicado'}</td>
                            <td data-label="Retenido">{euros(c.retenido_centimos)}</td>
                            <td data-label="Levantado efectivo">{final ? euros(levantadoEfectivo(c, fase)) : 'No aplica (F4)'}</td>
                            <td data-label="Neto final">{final ? euros(c.neto_centimos) : 'Pendiente de F6'}</td>
                            <td data-label="Resultado levantamiento">{final ? resultadoLevantamiento(c.resultado_levantamiento) : 'No aplica (F4)'}</td>
                        </tr>)}</tbody>
                    </table>
                </div>
            </div>
        </details>)}
    </div>;
}

export function EstadosBancarios({ historico }) {
    return <section className="card" aria-labelledby="rb-estados-heading">
        <div className="rb-section-heading">
            <h3 id="rb-estados-heading">Estado del procedimiento bancario</h3>
            <span className={`badge ${historico.estado_global === 'cerrado' ? 'badge-done' : 'badge-progress'}`}>
                Global: {historico.estado_global === 'cerrado' ? 'cerrado' : 'pendiente'}
            </span>
        </div>
        <p className="rb-help">El cierre procedimental y la recuperación son independientes. Una rama cerrada puede tener recuperación parcial o nula; no implica conciliación contable.</p>
        {historico.bancos.length ? <div className="rb-table-wrap" role="region" aria-label="Estados por banco" tabIndex={0}>
            <table className="data-table rb-table rb-bank-table">
                <caption>Ramas bancarias del expediente</caption>
                <thead><tr><th>Banco NRBE</th><th>Estado procedimental</th><th>Recuperación</th><th>Ordenado</th><th>Retenido</th><th>Neto final</th></tr></thead>
                <tbody>{historico.bancos.map(b => <tr key={b.nrbe}>
                    <td data-label="Banco NRBE"><strong>{b.nrbe}</strong></td>
                    <td data-label="Estado procedimental">{textoEstado(b.estado_procedimental)}</td>
                    <td data-label="Recuperación">{textoEstado(b.recuperacion)}</td>
                    <td data-label="Ordenado">{euros(b.ordenado_centimos)}</td>
                    <td data-label="Retenido">{historico.recepciones.some(r => r.nrbe === b.nrbe) ? euros(b.retenido_centimos) : 'No comunicado'}</td>
                    <td data-label="Neto final">{b.estado_procedimental === 'cerrado' ? euros(b.neto_centimos) : 'Pendiente de F6'}</td>
                </tr>)}</tbody>
            </table>
        </div> : <div className="rb-empty">No hay ramas bancarias disponibles. No se infiere el cierre del expediente.</div>}
    </section>;
}

function OriginalDownload({ expedienteId, recepcion }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const descargar = async () => {
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
            // Axios shares the app's authenticated header; never a bare public download URL.
            const { data } = await endpoints.n63.originalRetorno(expedienteId, recepcion.id);
            if (!(data instanceof Blob) || !data.size) throw new Error('No se ha recibido un original descargable.');
            const url = URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = (recepcion.nombre_original || `retorno-${recepcion.id}`).replace(/[\\/]/g, '_');
            document.body.appendChild(link);
            link.click();
            link.remove();
            // Delay revocation so the browser can start the download.
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            if (err.response?.data instanceof Blob) {
                try { err.response.data = JSON.parse(await err.response.data.text()); } catch { /* keep HTTP status */ }
            }
            setError(errorRetorno(err));
        } finally { setBusy(false); }
    };
    return <div className="rb-download">
        <button className="btn btn-sm btn-secondary" disabled={busy} onClick={descargar}>{busy ? 'Descargando…' : 'Descargar original'}</button>
        <ErrorMessage error={error} />
    </div>;
}

export function HistoricoRetornos({ recepciones, expedienteId }) {
    return <section className="card" aria-labelledby="rb-historico-heading">
        <h3 id="rb-historico-heading">Histórico inmutable <span className="rb-count">{recepciones.length} recepción(es)</span></h3>
        <p className="rb-help">Cada recepción conserva el original y su vínculo con la F3 exacta. No se habilitan edición, sustitución ni borrado. Un fichero distinto para la misma fase y banco es un conflicto, no un reemplazo.</p>
        {!recepciones.length && <div className="rb-empty">Todavía no se han confirmado retornos F4/F6.</div>}
        <div className="rb-history">
            {recepciones.map(r => <details className="rb-recepcion" key={r.id}>
                <summary><span><strong>{r.nombre_original || 'Original sin nombre'}</strong><span className="rb-subline">Recepción #{r.id} · F{r.fase} · Banco {r.nrbe}</span></span><span>{fechaRecepcion(r.created_at)}</span></summary>
                <div className="rb-recepcion-body">
                    <p>Recibido por <strong>{r.usuario || 'No informado'}</strong> · {fechaRecepcion(r.created_at)}</p>
                    <Hash label="SHA-256 del original" value={r.sha256} />
                    <Hash label="Huella de F3 vinculada" value={r.fase3_sha256} />
                    {r.fase3_revision != null && <p>Revisión F3: {r.fase3_revision}</p>}
                    {r.cabecera && <FechasCabecera cabecera={r.cabecera} fase={r.fase} />}
                    {Array.isArray(r.avisos) && r.avisos.length > 0 && <Avisos avisos={r.avisos} />}
                    {Number(r.fase) === 6 && !recepciones.some(other => Number(other.fase) === 4 && other.nrbe === r.nrbe) && <Avisos avisos={['F6 recibida sin F4 en el histórico. Ausencia documental; no se fabrica una retención F4 ni se exige una F5.']} />}
                    <ImportesRetorno data={r.resumen} fase={r.fase} />
                    <OriginalDownload expedienteId={expedienteId} recepcion={r} />
                    <DetallesRetorno detalles={r.detalles} fase={r.fase} />
                </div>
            </details>)}
        </div>
    </section>;
}

function FechasCabecera({ cabecera, fase }) {
    return <dl className="rb-facts">
        <div><dt>{Number(fase) === 6 ? 'Obtención por la entidad' : 'Obtención por el organismo'}</dt><dd>{fechaBancaria(cabecera.fecha_obtencion)}</dd></div>
        <div><dt>{Number(fase) === 6 ? 'Abono comunicado por la entidad' : 'Obtención por la entidad'}</dt><dd>{fechaBancaria(cabecera.fecha_entidad)}</dd></div>
        <div><dt>Emisor</dt><dd>{cabecera.nombre_emisor || 'No informado'}{cabecera.nif_emisor && ` · ${cabecera.nif_emisor}`}</dd></div>
    </dl>;
}

function Avisos({ avisos }) {
    return <div className="rb-message rb-warning"><strong>Avisos documentales</strong><ul>{avisos.map((a, i) => <li key={i}>{textoAviso(a)}</li>)}</ul></div>;
}

export default function ResultadosBancarios({ expedienteId, onConfirmed }) {
    const state = useRetornos(expedienteId, onConfirmed);
    const uploadId = useId();
    const [selectionError, setSelectionError] = useState(null);
    const confirming = state.busy === 'confirm';
    const choose = files => {
        if (confirming) return;
        if (files.length !== 1) {
            state.selectFile(null);
            setSelectionError({ message: 'Selecciona un solo original para revisar y confirmar su recepción.' });
            return;
        }
        setSelectionError(null);
        state.selectFile(files[0]);
    };
    const avisos = state.preview ? [...state.preview.avisos] : [];
    if (state.preview && Number(state.preview.fase) === 6 && state.historico
        && !state.historico.recepciones.some(r => Number(r.fase) === 4 && r.nrbe === state.preview.nrbe)
        && !avisos.some(a => /sin.?F4|sin.?Fase.?4/i.test(textoAviso(a)))) {
        avisos.push('F6 sin F4 recibida: ausencia documental no bloqueante. No se fabrica F4 ni se exige F5 si no hubo levantamientos.');
    }

    return <div className="rb-results" aria-busy={Boolean(state.busy || state.loading)}>
        <div className="rb-section-heading">
            <div><h2>Resultados bancarios</h2><p className="rb-help">Retenciones F4 y resultados finales F6, detectados por contenido.</p></div>
            <button className="btn btn-sm btn-secondary" onClick={state.refresh} disabled={Boolean(state.busy || state.loading)}>Actualizar histórico</button>
        </div>
        <div className="rb-message rb-accounting"><strong>Contabilización/conciliación en Odoo: fuera del alcance de esta app.</strong><div>Importar un retorno no crea PAY, pagos, asientos ni conciliaciones. El abono comunicado no demuestra conciliación en Odoo.</div></div>
        {state.loading && <p role="status">Cargando estado e histórico… La confirmación permanece bloqueada.</p>}
        <ErrorMessage error={state.historyError}><p>No se muestra un estado antiguo como vigente. Actualiza el histórico antes de confirmar.</p></ErrorMessage>
        {state.historico && !state.loading && <EstadosBancarios historico={state.historico} />}

        <section className="card" aria-labelledby="rb-upload-heading">
            <h3 id="rb-upload-heading">Recibir un original F4 / F6</h3>
            <p className="rb-help">La previsualización no persiste la recepción. Se admite cualquier extensión o ausencia de extensión; el servidor valida el contenido y su correspondencia con F3.</p>
            <div className="dropzone rb-upload" onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); choose(e.dataTransfer.files); }}>
                <label htmlFor={uploadId}>Seleccionar o arrastrar un único fichero bancario</label>
                <input id={uploadId} type="file" disabled={confirming} onChange={e => {
                    if (e.target.files.length) choose(e.target.files);
                    e.target.value = ''; // Allows selecting the same original again after a failed preview.
                }} />
            </div>
            {state.file && <div className="rb-file-selection"><strong>{state.file.name}</strong><span>{state.file.size.toLocaleString('es-ES')} bytes</span><button className="btn btn-sm btn-secondary" disabled={Boolean(state.busy)} onClick={() => state.runPreview(state.file)}>Previsualizar de nuevo</button></div>}
            {state.busy === 'preview' && <p role="status">Validando original y vínculo con F3… No se ha guardado ninguna recepción.</p>}
            <ErrorMessage error={selectionError} />
            <ErrorMessage error={state.error}>
                <p>{state.error?.status === 409 ? 'Conflicto o previsualización obsoleta: no se reemplaza ninguna evidencia. Revisa el histórico y vuelve a previsualizar.' : 'No confirmes a ciegas. Si el error ocurrió al confirmar, comprueba el histórico: la recepción podría haberse guardado antes del fallo de red. Una nueva previsualización permitirá detectar una reimportación idéntica.'}</p>
            </ErrorMessage>
            {state.notice && <div className="rb-message rb-success" role="status">{state.notice}</div>}
            {state.preview && <div className="rb-preview">
                <div className="rb-section-heading"><h3>Previsualización · F{state.preview.fase} · Banco {state.preview.nrbe}</h3><span className="badge badge-open">Validada por el servidor · sin guardar</span></div>
                <p><strong>{state.file?.name}</strong> · {state.preview.resumen.diligencias ?? state.preview.detalles.length} diligencia(s) · {state.preview.resumen.cuentas ?? 'No informado'} cuenta(s)</p>
                <Hash label="SHA-256 del original" value={state.preview.sha256} />
                <FechasCabecera cabecera={state.preview.cabecera} fase={state.preview.fase} />
                <ImportesRetorno data={state.preview.resumen} fase={state.preview.fase} />
                {Number(state.preview.fase) === 6 && <p className="rb-help">Levantado efectivo = retenido − neto. El importe solicitado puede no ejecutarse si el banco rechaza el levantamiento.</p>}
                {avisos.length > 0 && <Avisos avisos={avisos} />}
                {state.preview.duplicado && <div className="rb-message rb-warning">Este mismo original ya consta recibido. Confirmar será idempotente: no duplicará ni reemplazará la recepción.</div>}
                <DetallesRetorno detalles={state.preview.detalles} fase={state.preview.fase} />
                <div className="rb-confirmation">
                    <label><input type="checkbox" checked={state.acknowledged} disabled={confirming || state.loading || Boolean(state.historyError)} onChange={e => state.setAcknowledged(e.target.checked)} /><span>He revisado banco, fase, fechas, importes, diligencias/cuentas y avisos del fichero <strong>{state.file?.name}</strong>. Confirmo su recepción como evidencia inmutable, sin efectos contables en Odoo.</span></label>
                    <button className="btn btn-primary" disabled={!state.canConfirm} onClick={state.confirmPreview}>{confirming ? 'Confirmando recepción…' : state.preview.duplicado ? 'Confirmar reimportación idempotente' : 'Confirmar recepción del original'}</button>
                    <p className="rb-help">Se enviarán el mismo original y el token de esta previsualización. El servidor revalida F3; cualquier error invalida la confirmación y exige revisar de nuevo.</p>
                </div>
            </div>}
        </section>
        {state.historico && !state.loading && <HistoricoRetornos recepciones={state.historico.recepciones} expedienteId={expedienteId} />}
    </div>;
}
